import { useState } from 'react'
import { CreditCard, ExternalLink, FileText, LoaderCircle, Plus, ReceiptText, TriangleAlert, Wallet } from 'lucide-react'
import type { BillingView, BudgetView, PlanView } from '@takeit4me/shared'
import { platform, MOCK } from '../../api'
import { Panel } from '../../components/Card'
import { EmptyState } from '../../components/EmptyState'
import { Pill } from '../../components/Pill'
import { Async, Field, Meter, Modal, useToast } from '../../components/ui'
import { useQuery } from '../../lib/hooks'
import { dateOnly, money, num } from '../../lib/format'
import type { Tone } from '../../lib/labels'

export const BILLING_STATUS: Record<string, { label: string; tone: Tone; help: string }> = {
  none: { label: 'No plan yet', tone: 'gray', help: 'Choose a plan to go live.' },
  incomplete: { label: 'Waiting for payment', tone: 'amber', help: 'The first payment has not gone through yet.' },
  trialing: { label: 'Trial', tone: 'blue', help: 'Your trial ends at the end of this period.' },
  active: { label: 'Active', tone: 'green', help: 'Paid up.' },
  past_due: { label: 'Payment failed', tone: 'red', help: 'We could not take the last payment. Update your card before the grace period ends, or sending will pause.' },
  unpaid: { label: 'Unpaid', tone: 'red', help: 'The grace period has ended. Your account is suspended until the invoice is paid.' },
  canceled: { label: 'Cancelled', tone: 'gray', help: 'The subscription has ended.' },
}
const LEDGER_KIND: Record<string, Tone> = { grant: 'green', purchase: 'green', refund: 'blue', spend: 'gray', adjustment: 'violet', expiry: 'amber' }
const BUDGET_TONE: Record<BudgetView['state'], string> = { ok: '#16a34a', warning: '#d97706', exceeded: '#dc2626', unset: '#94a3b8' }
const usd = (micros: number) => `US$${(micros / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Billing for takeit4me itself (OP-04/05/06/10): plan, usage, invoices, credits and budgets. Admins only. */
export function BillingPage() {
  const q = useQuery(() => platform.billing(), [])
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="px-6 py-5 max-md:px-4 flex flex-col gap-4 max-w-[1240px]">
        <Async q={q}>{(b) => <BillingBody b={b} reload={() => q.reload(true)} setData={(x) => q.setData(x)} />}</Async>
      </div>
    </div>
  )
}

/** Opens a Stripe page: the browser leaves for Checkout or the portal and comes back here. */
function useStripeRedirect() {
  const toast = useToast()
  const [busy, setBusy] = useState<string | null>(null)
  const go = async (key: string, fn: () => Promise<{ url: string }>) => {
    setBusy(key)
    try {
      const { url } = await fn()
      if (MOCK) toast('Demo: this would open Stripe to finish.', 'info')
      else location.assign(url)
    } catch (e) { toast(e instanceof Error ? e.message : String(e), 'error') } finally { setBusy(null) }
  }
  return { busy, go }
}

function BillingBody({ b, reload, setData }: { b: BillingView; reload: () => void; setData: (b: BillingView) => void }) {
  const st = BILLING_STATUS[b.status] ?? BILLING_STATUS.none
  const { busy, go } = useStripeRedirect()
  const [choosing, setChoosing] = useState(false)
  return (
    <>
      {!b.configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-[12.5px] flex gap-2">
          <TriangleAlert size={16} className="shrink-0 mt-0.5" />
          <div><b>Online billing is not set up on this cell yet.</b> Your usage is still measured below. Contact takeit4me to change plan or buy credits.</div>
        </div>
      )}
      {(b.status === 'past_due' || b.status === 'unpaid') && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-[12.5px] flex gap-2 items-start">
          <TriangleAlert size={16} className="shrink-0 mt-0.5" />
          <div className="flex-1">{st.help}{b.grace_until && b.status === 'past_due' && <> Grace period ends <b>{dateOnly(b.grace_until, true)}</b>.</>}</div>
          <button className="btn sm pri" disabled={!b.configured || busy !== null} onClick={() => void go('portal', () => platform.billingPortal())}>{busy === 'portal' ? <LoaderCircle className="spin" /> : <CreditCard />} Update payment</button>
        </div>
      )}

      <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-4 max-xl:grid-cols-1">
        <Panel title="Plan" extra={<Pill tone={st.tone} title={st.help}>{st.label}</Pill>}
          actions={<>
            <button className="btn sm" disabled={!b.configured || busy !== null || b.status === 'none'} onClick={() => void go('portal', () => platform.billingPortal())}>{busy === 'portal' ? <LoaderCircle className="spin" /> : <CreditCard />} Manage payment</button>
            <button className="btn sm pri" disabled={!b.configured} onClick={() => setChoosing(true)}>{b.plan ? 'Change plan' : 'Choose a plan'}</button>
          </>}>
          {b.plan ? (
            <>
              <div className="flex items-baseline gap-2"><span className="text-[22px] font-extrabold">{b.plan.name}</span><span className="text-muted">{money(b.plan.monthly_minor, b.plan.currency)} a month</span></div>
              <div className="text-muted text-[12px] mb-2">{b.plan.description}</div>
              <PlanTerms p={b.plan} />
              {b.period.start && <div className="text-muted text-[12px] mt-2">This period: {dateOnly(b.period.start)} to {dateOnly(b.period.end, true)}</div>}
            </>
          ) : <EmptyState icon={<Wallet size={20} />} title="No plan yet">Choose a plan to go live. You can run in shadow mode before you pay.</EmptyState>}
        </Panel>

        <Panel title="Usage this period" extra={b.estimate_minor !== null && <Pill tone="brand">about {money(b.estimate_minor, b.currency)}</Pill>}>
          {b.usage.length === 0 ? <div className="text-muted text-[12.5px] py-2">No usage yet this period.</div> : b.usage.map((u) => (
            <div key={u.metric} className="kvrow">
              <span className="text-ink2">{u.label}</span>
              <span className="font-semibold text-right">
                {u.metric === 'voice_seconds' ? `${num(Math.round(u.quantity / 60))} min` : u.metric === 'ai_cost_micros' ? usd(u.quantity) : num(u.quantity)}
                {u.amount_minor !== null && <span className="text-muted font-normal"> · {money(u.amount_minor, b.currency)}</span>}
              </span>
            </div>
          ))}
          <div className="text-faint text-[11px] mt-2">The estimate is the plan plus resolved conversations and voice minutes so far. Stripe's invoice is the final word.</div>
        </Panel>
      </div>

      <div className="grid grid-cols-2 gap-4 max-xl:grid-cols-1">
        <Panel title="Task credits" extra={<Pill tone={b.credits.balance > 0 ? 'green' : 'red'}>{num(b.credits.balance)} left</Pill>}>
          <div className="text-muted text-[12px] mb-2">Your plan includes {num(b.credits.included_monthly)} credits a month. Agent tasks spend credits when they start; a task that never runs gives them back.</div>
          <div className="flex gap-2 flex-wrap mb-3">
            {b.packs.map((p) => (
              <button key={p.key} className="btn sm" disabled={!b.configured || !p.purchasable || busy !== null} onClick={() => void go(p.key, () => platform.buyCredits({ pack: p.key }))}>
                {busy === p.key ? <LoaderCircle className="spin" /> : <Plus />} {p.name} · {money(p.price_minor, p.currency)}
              </button>
            ))}
          </div>
          {b.credits.ledger.length === 0 ? <div className="text-muted text-[12.5px]">No credit movements yet.</div> : (
            <table className="tbl"><thead><tr><th>When</th><th>What</th><th className="text-right">Credits</th></tr></thead>
              <tbody>{b.credits.ledger.map((e) => (
                <tr key={e.id}><td className="whitespace-nowrap text-muted">{dateOnly(e.created_at)}</td><td><Pill tone={LEDGER_KIND[e.kind] ?? 'gray'}>{e.kind}</Pill> <span className="text-ink2">{e.reason}</span></td>
                  <td className={`text-right font-bold ${e.delta < 0 ? 'text-ink2' : 'text-green-700'}`}>{e.delta > 0 ? '+' : ''}{num(e.delta)}</td></tr>
              ))}</tbody></table>
          )}
        </Panel>
        <Panel title="Invoices">
          {b.invoices.length === 0 ? <EmptyState icon={<ReceiptText size={20} />} title="No invoices yet">Your first invoice arrives at the end of your first period.</EmptyState> : (
            <table className="tbl"><thead><tr><th>Invoice</th><th>Period</th><th>Status</th><th className="text-right">Amount</th><th /></tr></thead>
              <tbody>{b.invoices.map((i) => (
                <tr key={i.id}>
                  <td className="font-semibold">{i.number ?? 'Draft'}</td>
                  <td className="text-muted whitespace-nowrap">{i.period_start ? `${dateOnly(i.period_start)} – ${dateOnly(i.period_end)}` : dateOnly(i.created_at)}</td>
                  <td><Pill tone={i.status === 'paid' ? 'green' : i.status === 'open' ? 'amber' : i.status === 'uncollectible' ? 'red' : 'gray'}>{i.status}</Pill></td>
                  <td className="text-right font-semibold">{money(i.amount_due_minor, i.currency)}</td>
                  <td className="text-right whitespace-nowrap">
                    {i.hosted_url && <a className="btn sm ghost" href={i.hosted_url} target="_blank" rel="noreferrer" aria-label="Open invoice"><ExternalLink /></a>}
                    {i.pdf_url && <a className="btn sm ghost" href={i.pdf_url} target="_blank" rel="noreferrer" aria-label="Download PDF"><FileText /></a>}
                  </td>
                </tr>
              ))}</tbody></table>
          )}
        </Panel>
      </div>

      <Budgets b={b} onSaved={(budgets) => setData({ ...b, budgets })} />
      <ChoosePlan open={choosing} b={b} onClose={() => setChoosing(false)} go={go} busy={busy} onChanged={reload} />
    </>
  )
}

export function PlanTerms({ p }: { p: PlanView }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-[12px] max-sm:grid-cols-1">
      <div className="rounded-lg bg-bg px-2.5 py-1.5"><div className="text-muted">Per resolved conversation</div><b>{money(p.per_resolved_minor, p.currency, { cents: true })}</b></div>
      <div className="rounded-lg bg-bg px-2.5 py-1.5"><div className="text-muted">Voice minute</div><b>{money(p.voice_minute_minor, p.currency, { cents: true })}</b></div>
      <div className="rounded-lg bg-bg px-2.5 py-1.5"><div className="text-muted">Task credits a month</div><b>{num(p.included_credits)}</b></div>
    </div>
  )
}

function ChoosePlan({ open, b, onClose, go, busy }: { open: boolean; b: BillingView; onClose: () => void; go: (key: string, fn: () => Promise<{ url: string }>) => Promise<void>; busy: string | null; onChanged: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title={b.plan ? 'Change plan' : 'Choose a plan'} width={640}>
      <div className="text-muted text-[12.5px] mb-3">{b.plan ? 'A change takes effect now; Stripe prorates the difference on your next invoice.' : 'You pay by card through Stripe. You can change or cancel at any time.'}</div>
      <div className="flex flex-col gap-2">
        {b.plans.map((p) => (
          <div key={p.key} className={`card px-4 py-3 ${b.plan?.key === p.key ? 'border-brand2 bg-[#f5f7ff]' : ''}`}>
            <div className="flex items-center gap-2 mb-1">
              <b className="text-[14px]">{p.name}</b><span className="text-muted">{money(p.monthly_minor, p.currency)} a month</span>
              <span className="ml-auto">
                {b.plan?.key === p.key ? <Pill tone="brand">Current</Pill>
                  : <button className="btn sm pri" disabled={!p.purchasable || busy !== null} title={p.purchasable ? undefined : 'Not available online yet'}
                    onClick={() => void go(`plan:${p.key}`, () => (b.plan && b.status !== 'canceled' ? platform.billingPortal() : platform.billingCheckout({ plan: p.key })))}>
                    {busy === `plan:${p.key}` && <LoaderCircle className="spin" />} {b.plan ? 'Switch' : 'Choose'}
                  </button>}
              </span>
            </div>
            <div className="text-muted text-[12px] mb-2">{p.description}</div>
            <PlanTerms p={p} />
          </div>
        ))}
      </div>
    </Modal>
  )
}

/** Monthly spending limits (OP-10): AI models, phone minutes and task agents, in US dollars. Warn at 80%; stop at 100%. */
function Budgets({ b, onSaved }: { b: BillingView; onSaved: (budgets: BudgetView[]) => void }) {
  const toast = useToast()
  const [edit, setEdit] = useState<BudgetView | null>(null)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async (limit: number | null) => {
    if (!edit) return
    setBusy(true)
    try { onSaved(await platform.setBudget({ kind: edit.kind, limit_micros: limit })); toast(limit === null ? `${edit.label}: no limit` : `${edit.label}: limit saved`); setEdit(null) }
    catch (e) { toast(e instanceof Error ? e.message : String(e), 'error') } finally { setBusy(false) }
  }
  return (
    <Panel title="Budgets" extra={<span className="text-muted text-[12px]">We warn at 80% and stop the work at 100%, with an alarm.</span>}>
      <div className="grid grid-cols-3 gap-3 max-xl:grid-cols-1">
        {b.budgets.map((x) => (
          <div key={x.kind} className={`rounded-xl border px-3.5 py-3 ${x.state === 'exceeded' ? 'border-red-200 bg-red-50' : x.state === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-line'}`}>
            <div className="flex items-center gap-2 mb-1">
              <b className="text-[12.5px]">{x.label}</b>
              {x.state === 'warning' && <Pill tone="amber">Near the limit</Pill>}
              {x.state === 'exceeded' && <Pill tone="red">Limit reached</Pill>}
              <button className="btn sm ml-auto" onClick={() => { setEdit(x); setValue(x.limit_micros === null ? '' : String(x.limit_micros / 1_000_000)) }}>Edit</button>
            </div>
            <div className="text-[18px] font-extrabold">{usd(x.used_micros)} <span className="text-muted text-[12px] font-semibold">{x.limit_micros === null ? 'no limit set' : `of ${usd(x.limit_micros)}`}</span></div>
            {x.used_fraction !== null && <Meter value={x.used_fraction} color={BUDGET_TONE[x.state]} className="mt-1.5" />}
          </div>
        ))}
      </div>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.label ?? ''} footer={<>
        <button className="btn" disabled={busy} onClick={() => void save(null)}>No limit</button>
        <button className="btn pri" disabled={busy || value === '' || Number(value) < 0} onClick={() => void save(Math.round(Number(value) * 1_000_000))}>{busy && <LoaderCircle className="spin" />} Save limit</button>
      </>}>
        <Field label="Monthly limit (US dollars)" hint="Costs are recorded in US dollars, as the providers charge them.">
          <span className="flex items-center gap-2"><span className="text-muted">US$</span><input className="input w-40" type="number" min={0} step={10} value={value} onChange={(e) => setValue(e.target.value)} /></span>
        </Field>
      </Modal>
    </Panel>
  )
}
